import { SendOutlined } from "@ant-design/icons";
import type { Chat, Member } from "@di-devs/member-feature-plugin/shared";
import { useBetterFeatureClient } from "@di-devs/utils-fe";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Col, Form, Input, Modal, message, Row, Tooltip } from "antd";
import type { AxiosError } from "axios";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { BaseAnimationComponent, TapAnimationComponent } from "@/components";
import ManagerChatMessage from "@/components/common/ChatMessage/ManagerChatMessage";
import UserChatMessage from "@/components/common/ChatMessage/UserChatMessage";
import type { FeatureClient } from "@/config/client";
import { useSSEEvent } from "@/context/SSEContext";
import { API, COMMONS } from "@/utils";

const { TextArea } = Input;

interface ChatModalProps {
	publicSettings: any;
	currentMember: Member | null;
	isChatModalVisible: boolean;
	hideChatModal: () => void;
}

const ChatModal = (props: ChatModalProps) => {
	const { publicSettings, currentMember, isChatModalVisible, hideChatModal } =
		props;

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const currentMemberRef = useRef<Member | null>(null);
	const messageContainerRef = useRef<any>(null);
	const [chatForm] = Form.useForm();

	const [chats, setChats] = useState<Chat[]>([]);

	const scrollToMessageContainer = () => {
		if (messageContainerRef.current) {
			setTimeout(() => {
				messageContainerRef?.current?.scrollIntoView({ behavior: "smooth" });
			}, 100);
		}
	};

	const chatsQuery = useQuery({
		queryKey: [API.QUERY_KEY_ADMIN_CHATS, currentMember?.memberId],
		queryFn: () => reactFeatureClient.getChat(currentMember?.memberId),
		enabled: !!(currentMember?.memberId && isChatModalVisible),
	});

	useEffect(() => {
		if (chatsQuery.isSuccess && chatsQuery.data) {
			const response = chatsQuery.data;
			if (isMountedRef.current) {
				setChats(response?.data?.chats || []);
				scrollToMessageContainer();
			}
		}
	}, [chatsQuery.isSuccess, chatsQuery.data]);

	useEffect(() => {
		if (chatsQuery.isError) {
			const error = chatsQuery.error as AxiosError;
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		}
	}, [chatsQuery.isError, chatsQuery.error]);

	const sendChatMutation = useMutation({
		mutationFn: (data: { contents: string; memberId: Member["memberId"] }) =>
			reactFeatureClient.replyChat(data.memberId, {
				contents: data.contents,
				contentType: "text",
			}), // TODO: Support other content types
		onSuccess: () => {
			message.success(COMMONS.SUCCESS_MESSAGE_SENT_MSG);
			queryClient.invalidateQueries({ queryKey: [API.QUERY_KEY_ADMIN_CHATS] });
			chatForm.resetFields();
		},
		onError: (error: AxiosError) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	useEffect(() => {
		currentMemberRef.current = currentMember;
	}, [currentMember]);

	const chatHandler = useCallback(
		(data: { memberId: number; type: string; chat?: Chat }) => {
			console.log(
				"[ChatModal] SSE chat event received:",
				data,
				"currentMember:",
				currentMemberRef.current?.memberId,
			);
			if (data !== undefined && Object.keys(data).length !== 0) {
				if (data?.memberId === currentMemberRef.current?.memberId) {
					console.log("[ChatModal] memberId matched");
					// Handle different types of chat events
					if (data?.type === "read") {
						// Messages were marked as read - update unread counts in member list
						queryClient.setQueriesData(
							{ queryKey: [API.QUERY_KEY_ADMIN_MEMBERS] },
							(oldData: any) => {
								if (!oldData || !oldData.data || !oldData.data.rows) return oldData;
								const newRows = oldData.data.rows.map((member: any) => {
									if (member.memberId === data.memberId) {
										return { ...member, unreadCount: 0 };
									}
									return member;
								});
								return { ...oldData, data: { ...oldData.data, rows: newRows } };
							}
						);
					} else {
						// New message received
						if (data.chat) {
							console.log("[ChatModal] Appending new chat message to cache", data.chat);
							queryClient.setQueryData(
								[API.QUERY_KEY_ADMIN_CHATS, currentMemberRef.current?.memberId],
								(oldData: any) => {
									if (!oldData || !oldData.data || !oldData.data.chats) return oldData;
									return {
										...oldData,
										data: {
											...oldData.data,
											chats: [...oldData.data.chats, data.chat]
										}
									};
								}
							);
							// Also scroll to bottom
							scrollToMessageContainer();
						} else {
							// Fallback
							console.log(
								"[ChatModal] No chat data in event - refreshing chat history",
							);
							queryClient.invalidateQueries({
								queryKey: [API.QUERY_KEY_ADMIN_CHATS],
							});
						}
						// Always refresh member list for unread count/last message update
						queryClient.setQueriesData(
							{ queryKey: [API.QUERY_KEY_ADMIN_MEMBERS] },
							(oldData: any) => {
								if (!oldData || !oldData.data || !oldData.data.rows) return oldData;
								const newRows = oldData.data.rows.map((member: any) => {
									if (member.memberId === data.memberId) {
										return { 
											...member, 
											unreadCount: 0,
											lastMessageAt: data.chat?.createdAt || new Date().toISOString()
										};
									}
									return member;
								});
								return { ...oldData, data: { ...oldData.data, rows: newRows } };
							}
						);
					}
				} else {
					console.log(
						"[ChatModal] memberId mismatch — ignoring. event:",
						data?.memberId,
						"current:",
						currentMemberRef.current?.memberId,
					);
				}
			}
		},
		[queryClient],
	);

	useSSEEvent(API.SSE_CHAT, chatHandler);

	const handleChat = (data: any) => {
		const paramData = {
			contents: data?.contents,
			memberId: currentMember?.memberId,
		};

		sendChatMutation.mutate(paramData);
	};

	return (
		<Modal
			title={`${
				currentMember?.lastName && currentMember?.firstName
					? `${currentMember?.lastName || "ー"} ${currentMember?.firstName || "ー"}`
					: currentMember?.displayName || "ー"
			}様`}
			open={isChatModalVisible}
			onCancel={hideChatModal}
			footer={null}
			centered
			destroyOnClose
		>
			<BaseAnimationComponent>
				<motion.div
					className="pb-4 overflow-auto"
					style={{
						minHeight: "60vh",
						maxHeight: "60vh",
					}}
				>
					{chats.map((chat) => (
						<Row key={chat?.chatId}>
							<Col span={22} offset={chat?.source === "user" ? 0 : 2}>
								<div className="m-1">
									{chat?.source === "user" ? (
										<UserChatMessage
											chat={chat}
											currentMember={currentMember}
										/>
									) : (
										<ManagerChatMessage
											chat={chat}
											publicSettings={publicSettings}
										/>
									)}
								</div>
							</Col>
						</Row>
					))}
					<div ref={messageContainerRef}></div>
				</motion.div>
				<div
					className="py-4 border-t-2 border-solid"
					style={{
						borderColor: publicSettings?.PRIMARY_COLOR?.valueString,
					}}
				>
					<Row>
						<Col span={24}>
							<Form
								form={chatForm}
								onFinish={handleChat}
								size="large"
								colon={false}
								requiredMark={false}
								initialValues={{
									contents: undefined,
								}}
							>
								<Row align="top" gutter={[8, 8]}>
									<Col span={24}>
										<div className="flex justify-end">
											<Tooltip title="送信" placement="top">
												<TapAnimationComponent>
													<Button
														type="primary"
														size="large"
														style={{
															borderColor: COMMONS.WHITE_COLOR,
														}}
														icon={
															<SendOutlined
																style={{
																	color: COMMONS.WHITE_COLOR,
																}}
															/>
														}
														htmlType="submit"
														loading={sendChatMutation.isLoading}
													/>
												</TapAnimationComponent>
											</Tooltip>
										</div>
									</Col>
									<Col span={24}>
										<Form.Item
											className="block"
											name="contents"
											rules={[
												{
													required: true,
													whitespace: true,
													message: "メッセージを入力してください",
												},
											]}
										>
											<TextArea
												allowClear
												bordered
												showCount
												autoSize={{ minRows: 5, maxRows: 10 }}
												maxLength={5000}
												placeholder="メッセージを入力してください"
												autoFocus={true}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</Col>
					</Row>
				</div>
			</BaseAnimationComponent>
		</Modal>
	);
};

export default ChatModal;
